import React from "react";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";

/* ───────────────────── Global Font ───────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.8/dist/web/variable/pretendardvariable-dynamic-subset.css');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, sans-serif; }
`;

/* ───────────────────── Tokens ───────────────────── */
const C = {
  primary: "#2563EB", primaryDark: "#1E40AF",
  accent: "#10B981", accentDark: "#059669",
  gray50: "#F9FAFB", gray100: "#F3F4F6", gray200: "#E5E7EB",
  gray500: "#6B7280", gray700: "#374151", gray900: "#111827",
  white: "#FFFFFF",
};
const gradientText = css`background: linear-gradient(135deg, ${C.primary}, ${C.accent}); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;`;
const gradientBg = `linear-gradient(135deg, ${C.primary}, ${C.primaryDark}, ${C.accent})`;
const MAX_W = "1200px";
const KAKAO = "http://pf.kakao.com/_IkjxhX/chat";
const fadeUp = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`;

/* ───────────────────── Shared Styled ───────────────────── */
const Section = styled.section<{bg?:string}>`padding:80px 24px; background:${p=>p.bg||C.white}; @media(max-width:768px){padding:56px 16px;}`;
const Container = styled.div`max-width:${MAX_W}; margin:0 auto;`;
const SectionTitle = styled.h2`font-size:36px; font-weight:800; text-align:center; color:${C.gray900}; margin-bottom:12px; @media(max-width:768px){font-size:28px;}`;
const SectionSub = styled.p`font-size:18px; text-align:center; color:${C.gray500}; margin-bottom:48px; @media(max-width:768px){font-size:15px; margin-bottom:36px;}`;
const GradientSpan = styled.span`${gradientText}`;
const CtaBtn = styled.a`display:inline-flex; align-items:center; gap:8px; padding:16px 36px; border-radius:16px; background:${gradientBg}; color:#fff; font-size:17px; font-weight:700; text-decoration:none; transition:all .3s; box-shadow:0 4px 24px rgba(37,99,235,.3); &:hover{transform:translateY(-2px); box-shadow:0 8px 32px rgba(37,99,235,.4);}`;

/* ═══════════════════════ 1. HEADER ═══════════════════════ */
const HeaderWrap = styled.header`position:sticky; top:0; z-index:100; background:rgba(255,255,255,.92); backdrop-filter:blur(12px); border-bottom:1px solid ${C.gray100};`;
const HeaderInner = styled.div`max-width:${MAX_W}; margin:0 auto; display:flex; align-items:center; justify-content:space-between; padding:0 24px; height:72px;`;
const Logo = styled.a`font-size:24px; font-weight:900; text-decoration:none; ${gradientText}`;
const NavLinks = styled.nav`display:flex; align-items:center; gap:32px; @media(max-width:768px){display:none;}`;
const NavA = styled.a`font-size:14px; font-weight:500; color:${C.gray700}; text-decoration:none; &:hover{color:${C.primary};}`;
const LoginBtn = styled.a`padding:10px 24px; border-radius:12px; background:${gradientBg}; color:#fff; font-size:14px; font-weight:600; text-decoration:none; transition:all .2s; &:hover{transform:translateY(-1px); box-shadow:0 4px 16px rgba(37,99,235,.3);}`;

function Header() {
  return (
    <HeaderWrap>
      <HeaderInner>
        <Logo href="/">Jumpin</Logo>
        <NavLinks>
          <NavA href="#features">서비스 소개</NavA>
          <NavA href="#how-it-works">이용 방법</NavA>
          <NavA href="#pricing">요금제</NavA>
          <NavA href={KAKAO} target="_blank" rel="noopener">문의하기</NavA>
          <LoginBtn href="/login">로그인</LoginBtn>
        </NavLinks>
      </HeaderInner>
    </HeaderWrap>
  );
}

/* ═══════════════════════ 2. HERO ═══════════════════════ */
const HeroSection = styled(Section)`padding-top:120px; padding-bottom:80px; text-align:center;`;
const HeroBadge = styled.span`display:inline-block; padding:8px 20px; border-radius:999px; background:${C.primary}10; color:${C.primary}; font-size:14px; font-weight:600; margin-bottom:32px;`;
const HeroH1 = styled.h1`font-size:52px; font-weight:900; line-height:1.2; color:${C.gray900}; margin-bottom:24px; animation:${fadeUp} .8s ease; @media(max-width:768px){font-size:32px;}`;
const HeroSub = styled.p`font-size:18px; color:${C.gray500}; line-height:1.7; margin-bottom:40px; white-space:pre-line; @media(max-width:768px){font-size:15px;}`;
const Promo = styled.p`margin-top:24px; font-size:14px; color:${C.accent}; font-weight:600;`;

/* ═══════════════════════ 3. TRUST / STATS ═══════════════════════ */
const StatsGrid = styled.div`display:grid; grid-template-columns:repeat(4,1fr); gap:24px; @media(max-width:768px){grid-template-columns:repeat(2,1fr);}`;
const StatCard = styled.div`text-align:center; padding:32px 16px; border-radius:20px; background:${C.white}; border:1px solid ${C.gray200}; transition:all .3s; &:hover{transform:translateY(-4px); box-shadow:0 12px 32px rgba(0,0,0,.06);}`;
const StatIcon = styled.div`font-size:32px; margin-bottom:12px;`;
const StatValue = styled.div`font-size:28px; font-weight:800; ${gradientText} margin-bottom:4px;`;
const StatLabel = styled.div`font-size:14px; font-weight:600; color:${C.gray700}; margin-bottom:4px;`;
const StatDesc = styled.div`font-size:12px; color:${C.gray500};`;

const STATS = [
  { icon: "📱", value: "6개", label: "지원 채널", desc: "도우인·샤오홍슈·티몰·징동·위챗·콰이쇼우" },
  { icon: "📦", value: "160,000+", label: "월 처리 주문", desc: "자동 주문 수집 & 관리" },
  { icon: "🤝", value: "200+", label: "셀러 네트워크", desc: "검증된 해외 셀러 매칭" },
  { icon: "🌏", value: "24/7", label: "실시간 운영", desc: "크로스보더 자동화" },
];

/* ═══════════════════════ 4. HOW IT WORKS ═══════════════════════ */
const StepsGrid = styled.div`display:grid; grid-template-columns:repeat(3,1fr); gap:32px; margin-bottom:48px; @media(max-width:768px){grid-template-columns:1fr;}`;
const StepCard = styled.div<{grad:string}>`border-radius:24px; overflow:hidden; background:${C.white}; border:1px solid ${C.gray200}; transition:all .3s; &:hover{transform:translateY(-4px); box-shadow:0 12px 40px rgba(0,0,0,.08);}`;
const StepHeader = styled.div<{grad:string}>`padding:24px; background:linear-gradient(135deg,${p=>p.grad}); color:#fff;`;
const StepNum = styled.div`width:36px; height:36px; border-radius:50%; background:rgba(255,255,255,.2); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:16px; margin-bottom:12px;`;
const StepTitle = styled.h3`font-size:20px; font-weight:700;`;
const StepBody = styled.div`padding:24px;`;
const StepDesc = styled.p`font-size:14px; color:${C.gray500}; line-height:1.7;`;

const STEPS = [
  { title: "채널 연동 & 셀러 매칭", desc: "판매할 채널을 선택하면, Jumpin이 최적의 해외 셀러를 매칭해 드립니다. 도우인부터 콰이쇼우까지 원하는 채널을 골라 입점하세요.", icon: "🔗", grad: "#60A5FA, #1D4ED8" },
  { title: "주문·재고·CS 자동화", desc: "상품 등록, 주문 수집, 재고 동기화, 배송 추적, CS 응대까지 — 모든 채널 운영이 하나의 대시보드에서 자동으로 처리됩니다.", icon: "🤖", grad: "#1E40AF, #2563EB" },
  { title: "정산 & 성과 분석", desc: "채널별 매출·수수료·정산을 자동 집계합니다. 실시간 대시보드로 성과를 분석하고, 데이터 기반으로 운영을 최적화하세요.", icon: "📊", grad: "#34D399, #059669" },
];

/* ═══════════════════════ 5. FEATURES ═══════════════════════ */
const FeatGrid = styled.div`display:grid; grid-template-columns:repeat(2,1fr); gap:24px; @media(max-width:768px){grid-template-columns:1fr;}`;
const FeatCard = styled.div`padding:32px; border-radius:24px; background:${C.white}; border:1px solid ${C.gray200}; transition:all .3s; &:hover{transform:translateY(-4px); box-shadow:0 12px 32px rgba(0,0,0,.06);}`;
const FeatIcon = styled.div<{g1:string;g2:string}>`width:56px; height:56px; border-radius:16px; background:linear-gradient(135deg,${p=>p.g1},${p=>p.g2}); display:flex; align-items:center; justify-content:center; font-size:28px; margin-bottom:20px;`;
const FeatTitle = styled.h3`font-size:20px; font-weight:700; color:${C.gray900}; margin-bottom:12px;`;
const FeatDesc = styled.p`font-size:14px; color:${C.gray500}; line-height:1.7;`;

const FEATS = [
  { icon: "📱", title: "6대 채널 통합 운영", desc: "도우인, 샤오홍슈, 티몰, 징동, 위챗, 콰이쇼우 — 각 플랫폼을 따로 관리할 필요 없이, 하나의 대시보드에서 상품·주문·재고를 통합 운영합니다.", g: ["#2563EB","#1E40AF"] },
  { icon: "🤝", title: "해외 셀러 매칭", desc: "검증된 중국 현지 셀러 네트워크를 통해 최적의 판매 파트너를 매칭해 드립니다. 직접 셀러를 찾을 필요 없이 바로 판매를 시작하세요.", g: ["#10B981","#059669"] },
  { icon: "🤖", title: "주문·CS 자동화", desc: "주문 수집, 배송 추적, 재고 동기화, CS 응대까지 AI가 24시간 자동으로 처리합니다. 복잡한 운영 업무를 95% 줄여드립니다.", g: ["#8B5CF6","#7C3AED"] },
  { icon: "📊", title: "실시간 정산·분석", desc: "채널별 매출, 수수료, 정산 현황을 실시간으로 확인합니다. 인기 상품 분석과 운영 최적화 리포트를 자동으로 제공합니다.", g: ["#F59E0B","#D97706"] },
];

/* ═══════════════════════ 6. PRICING ═══════════════════════ */
const PriceGrid = styled.div`display:grid; grid-template-columns:repeat(3,1fr); gap:24px; @media(max-width:768px){grid-template-columns:1fr;}`;
const PriceCard = styled.div<{hl?:boolean}>`border-radius:24px; background:${C.white}; border:${p=>p.hl?`2px solid ${C.primary}`:`1px solid ${C.gray200}`}; padding:36px 28px; position:relative; transition:all .3s; ${p=>p.hl?`box-shadow:0 8px 40px rgba(37,99,235,.12);`:""} &:hover{transform:translateY(-4px); box-shadow:0 16px 48px rgba(0,0,0,.08);}`;
const PriceBadge = styled.span`position:absolute; top:-14px; right:24px; padding:6px 16px; border-radius:999px; background:${gradientBg}; color:#fff; font-size:12px; font-weight:700;`;
const PriceName = styled.h3`font-size:22px; font-weight:700; color:${C.gray900}; margin-bottom:16px;`;
const PriceAmt = styled.div`font-size:32px; font-weight:900; ${gradientText} margin-bottom:24px;`;
const PriceUnit = styled.span`font-size:14px; color:${C.gray500}; font-weight:400;`;
const PriceFeatures = styled.ul`list-style:none; padding:0; margin:0 0 32px;`;
const PriceFeat = styled.li`padding:8px 0; border-bottom:1px solid ${C.gray100}; font-size:14px; color:${C.gray700}; display:flex; align-items:center; gap:8px; &::before{content:"✓"; color:${C.accent}; font-weight:700;}`;
const PriceBtn = styled.a<{hl?:boolean}>`display:block; text-align:center; padding:14px; border-radius:14px; font-weight:600; font-size:15px; text-decoration:none; transition:all .2s; ${p=>p.hl?`background:${gradientBg}; color:#fff;`:`background:${C.gray50}; color:${C.gray700}; border:1px solid ${C.gray200};`} &:hover{transform:translateY(-1px);}`;
const PriceNote = styled.p`text-align:center; margin-top:24px; font-size:13px; color:${C.gray500};`;

const PLANS = [
  { name: "Basic", price: "500,000", badge: null, hl: false, feats: ["채널 1개 운영","상품 등록 자동화","주문 수집 & 배송 추적","기본 정산 리포트"], cta: "상담 신청" },
  { name: "Pro", price: "800,000", badge: "추천", hl: true, feats: ["채널 2개 이상 운영","해외 셀러 매칭","AI 운영 자동화","재고 통합 동기화","실시간 성과 분석","전담 매니저"], cta: "상담 신청" },
  { name: "Enterprise", price: "1,800,000", badge: "대규모", hl: false, feats: ["채널 4개 이상 운영","Pro 기능 전체","커스텀 API 연동","전용 운영 환경","온보딩 교육","SLA 보장","24/7 기술 지원"], cta: "문의하기" },
];

/* ═══════════════════════ 7. CTA BANNER ═══════════════════════ */
const CtaBanner = styled(Section)`background:${gradientBg}; text-align:center; padding:80px 24px;`;
const CtaTitle = styled.h2`font-size:36px; font-weight:800; color:#fff; margin-bottom:12px; @media(max-width:768px){font-size:28px;}`;
const CtaSubtitle = styled.p`font-size:18px; color:rgba(255,255,255,.8); margin-bottom:36px;`;
const CtaWhiteBtn = styled.a`display:inline-flex; align-items:center; gap:8px; padding:18px 40px; border-radius:16px; background:#fff; color:${C.primary}; font-size:17px; font-weight:700; text-decoration:none; transition:all .3s; box-shadow:0 4px 24px rgba(0,0,0,.15); &:hover{transform:translateY(-2px); box-shadow:0 8px 32px rgba(0,0,0,.2);}`;

/* ═══════════════════════ 8. FOOTER ═══════════════════════ */
const FooterWrap = styled.footer`background:${C.gray900}; color:${C.gray500}; padding:64px 24px 32px;`;
const FooterInner = styled.div`max-width:${MAX_W}; margin:0 auto;`;
const FooterTop = styled.div`display:grid; grid-template-columns:2fr 1fr 1fr; gap:48px; margin-bottom:48px; @media(max-width:768px){grid-template-columns:1fr; gap:32px;}`;
const FooterLogo = styled.div`font-size:24px; font-weight:900; color:#fff; margin-bottom:16px;`;
const FooterDesc = styled.p`font-size:14px; line-height:1.7; color:${C.gray500};`;
const FooterColTitle = styled.h4`font-size:14px; font-weight:700; color:${C.white}; margin-bottom:16px;`;
const FooterLink = styled.a`display:block; font-size:13px; color:${C.gray500}; text-decoration:none; margin-bottom:10px; &:hover{color:${C.white};}`;
const FooterDivider = styled.div`border-top:1px solid rgba(255,255,255,.1); padding-top:24px;`;
const FooterBiz = styled.div`font-size:12px; color:rgba(255,255,255,.35); line-height:1.8;`;
const FooterCopy = styled.div`font-size:12px; color:rgba(255,255,255,.3); margin-top:12px;`;

/* ═══════════════════════ HOME ═══════════════════════ */
export default function Home() {
  return (
    <>
      <GlobalStyle />

      {/* 1. Header */}
      <Header />

      {/* 2. Hero */}
      <HeroSection>
        <Container>
          <HeroBadge>🇨🇳 중국 6대 플랫폼 올인원 운영 자동화</HeroBadge>
          <HeroH1>
            수출 전문 AI 에이전트로<br />
            <GradientSpan>쉽게 점핀하세요!</GradientSpan>
          </HeroH1>
          <HeroSub>
            {"도우인 · 샤오홍슈 · 티몰 · 징동 · 위챗 · 콰이쇼우\n주문·재고·정산 운영 자동화부터 해외 셀러 매칭까지"}
          </HeroSub>
          <CtaBtn href={KAKAO} target="_blank" rel="noopener">입점 상담 신청 →</CtaBtn>
          <Promo>💡 현재 얼리버드 입점사 모집 중!</Promo>
        </Container>
      </HeroSection>

      {/* 3. Trust / Stats */}
      <Section bg={C.gray50} id="features">
        <Container>
          <SectionTitle><GradientSpan>6대 채널</GradientSpan> 올인원 운영</SectionTitle>
          <SectionSub>복잡한 크로스보더 운영, Jumpin 하나로 해결됩니다</SectionSub>
          <StatsGrid>
            {STATS.map(s => (
              <StatCard key={s.label}>
                <StatIcon>{s.icon}</StatIcon>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
                <StatDesc>{s.desc}</StatDesc>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </Section>

      {/* 4. How It Works */}
      <Section id="how-it-works">
        <Container>
          <SectionTitle><GradientSpan>3단계</GradientSpan>로 시작하세요</SectionTitle>
          <SectionSub>복잡한 중국 채널 운영, 이렇게 간단해집니다</SectionSub>
          <StepsGrid>
            {STEPS.map((s, i) => (
              <StepCard key={i} grad={s.grad}>
                <StepHeader grad={s.grad}>
                  <StepNum>{i + 1}</StepNum>
                  <div style={{fontSize: 24, marginBottom: 8}}>{s.icon}</div>
                  <StepTitle>{s.title}</StepTitle>
                </StepHeader>
                <StepBody>
                  <StepDesc>{s.desc}</StepDesc>
                </StepBody>
              </StepCard>
            ))}
          </StepsGrid>
          <div style={{textAlign: "center"}}>
            <CtaBtn href={KAKAO} target="_blank" rel="noopener">입점 상담 신청 →</CtaBtn>
          </div>
        </Container>
      </Section>

      {/* 5. Features */}
      <Section bg={C.gray50}>
        <Container>
          <SectionTitle>왜 <GradientSpan>Jumpin</GradientSpan>인가요?</SectionTitle>
          <SectionSub>중국 수출의 모든 것을 하나의 플랫폼에서</SectionSub>
          <FeatGrid>
            {FEATS.map(f => (
              <FeatCard key={f.title}>
                <FeatIcon g1={f.g[0]} g2={f.g[1]}>{f.icon}</FeatIcon>
                <FeatTitle>{f.title}</FeatTitle>
                <FeatDesc>{f.desc}</FeatDesc>
              </FeatCard>
            ))}
          </FeatGrid>
        </Container>
      </Section>

      {/* 6. Pricing */}
      <Section id="pricing">
        <Container>
          <SectionTitle><GradientSpan>맞춤형</GradientSpan> 구독 서비스</SectionTitle>
          <SectionSub>채널 수와 운영 규모에 따라 최적의 플랜을 선택하세요</SectionSub>
          <PriceGrid>
            {PLANS.map(p => (
              <PriceCard key={p.name} hl={p.hl}>
                {p.badge && <PriceBadge>{p.badge}</PriceBadge>}
                <PriceName>{p.name}</PriceName>
                <PriceAmt>{p.price}<PriceUnit>원/월</PriceUnit></PriceAmt>
                <PriceFeatures>
                  {p.feats.map(f => <PriceFeat key={f}>{f}</PriceFeat>)}
                </PriceFeatures>
                <PriceBtn href={KAKAO} target="_blank" rel="noopener" hl={p.hl}>{p.cta}</PriceBtn>
              </PriceCard>
            ))}
          </PriceGrid>
          <PriceNote>* 모든 요금은 VAT 별도입니다.</PriceNote>
        </Container>
      </Section>

      {/* 7. CTA Banner */}
      <CtaBanner>
        <Container>
          <div style={{fontSize: 48, marginBottom: 16}}>🚀</div>
          <CtaTitle>중국 시장, 지금 시작하세요</CtaTitle>
          <CtaSubtitle>입점 상담을 통해 최적의 채널과 셀러를 매칭해 드립니다.</CtaSubtitle>
          <CtaWhiteBtn href={KAKAO} target="_blank" rel="noopener">💬 입점 상담 신청</CtaWhiteBtn>
        </Container>
      </CtaBanner>

      {/* 8. Footer — 점핀주식회사 정보 */}
      <FooterWrap>
        <FooterInner>
          <FooterTop>
            <div>
              <FooterLogo>Jumpin</FooterLogo>
              <FooterDesc>
                중국 수출 채널 운영 자동화 &amp; 해외 셀러 매칭 플랫폼.<br />
                도우인·샤오홍슈·티몰·징동·위챗·콰이쇼우를 하나로 관리합니다.
              </FooterDesc>
              <div style={{marginTop: 16}}>
                <FooterLink href="mailto:biz@jumpin.kr">📧 biz@jumpin.kr</FooterLink>
              </div>
            </div>
            <div>
              <FooterColTitle>서비스</FooterColTitle>
              <FooterLink href="#features">채널 통합 운영</FooterLink>
              <FooterLink href="#features">셀러 매칭</FooterLink>
              <FooterLink href="#pricing">요금제</FooterLink>
            </div>
            <div>
              <FooterColTitle>고객지원</FooterColTitle>
              <FooterLink href="/terms">이용약관</FooterLink>
              <FooterLink href="/privacy">개인정보처리방침</FooterLink>
            </div>
          </FooterTop>
          <FooterDivider />
          <FooterBiz>
            점핀주식회사 | 대표: 전지혜 | 사업자등록번호: 127-81-56735<br />
            광주광역시 임방울대로154-19 1층<br />
            이메일: biz@jumpin.kr
          </FooterBiz>
          <FooterCopy>© {new Date().getFullYear()} Jumpin. All rights reserved.</FooterCopy>
        </FooterInner>
      </FooterWrap>
    </>
  );
}
